import React from 'react';
import { RushLeaderboardEntry } from '../../types/triviaRush';
import { RushLeaderboardRow } from './RushLeaderboardRow';

interface Props {
  entries: RushLeaderboardEntry[];
  currentUser: { rank: number; highest_streak: number } | null;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  isAuthenticated: boolean;
  onSignInClick: () => void;
}

export const RushLeaderboard: React.FC<Props> = ({
  entries,
  currentUser,
  isLoading,
  hasMore,
  loadMore,
  loadingMore,
  isAuthenticated,
  onSignInClick,
}) => {
  return (
    <div className="flex flex-col gap-4 flex-1">
      {/* Heading */}
      <h2
        className="text-center font-serif text-2xl tracking-[0.15em] uppercase rush-heading-glow"
        style={{ color: 'hsl(var(--foreground))' }}
      >
        HIGH SCORES
      </h2>

      {/* Your best score */}
      {isAuthenticated && currentUser && (
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg"
          style={{
            backgroundColor: 'hsl(var(--muted))',
            border: '1px solid hsl(var(--border))',
          }}
        >
          <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Your Best
          </span>
          <div className="flex items-center gap-3">
            <span
              className="text-lg font-mono font-bold"
              style={{ color: 'var(--rush-purple-accent)' }}
            >
              {currentUser.highest_streak}
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.15)',
                color: 'var(--rush-purple-accent)',
              }}
            >
              #{currentUser.rank}
            </span>
          </div>
        </div>
      )}

      {/* Table header */}
      <div className="flex items-center gap-3 px-3 pb-1">
        <span
          className="w-8 text-center text-xs font-medium uppercase"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          #
        </span>
        <span
          className="flex-1 text-xs font-medium uppercase"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          Player
        </span>
        <span
          className="text-xs font-medium uppercase"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          Score
        </span>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-11 rounded-lg animate-pulse"
              style={{ backgroundColor: 'hsl(var(--card))' }}
            />
          ))}
        </div>
      )}

      {/* Leaderboard rows - relative container for anonymous overlay */}
      {!isLoading && (
        <div className="relative flex-1">
          <div className="flex flex-col gap-1.5">
            {entries.map((entry) => (
              <RushLeaderboardRow
                key={entry.user_id}
                entry={entry}
                isBlurred={!isAuthenticated}
              />
            ))}
          </div>

          {/* Anonymous overlay */}
          {!isAuthenticated && entries.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="text-center p-6 rounded-xl max-w-xs mx-auto"
                style={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <p
                  className="text-sm font-medium mb-4 leading-relaxed"
                  style={{ color: 'hsl(var(--foreground))' }}
                >
                  Sign in or create an account to view high scores and rank yourself
                </p>
                <button
                  onClick={onSignInClick}
                  className="px-5 py-2.5 rounded-lg text-sm font-bold transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, var(--rush-purple-mid), #7C3AED)',
                    color: 'white',
                  }}
                >
                  Sign In
                </button>
                <p
                  className="text-sm font-medium mt-4 leading-relaxed"
                  style={{ color: 'hsl(var(--foreground))' }}
                >
                  Use your school or work email to rep your team
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Load More */}
      {!isLoading && isAuthenticated && hasMore && entries.length > 0 && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mx-auto px-5 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{
            backgroundColor: 'hsl(var(--card))',
            color: 'var(--rush-purple-accent)',
            border: '1px solid hsl(var(--border))',
          }}
        >
          {loadingMore ? 'Loading...' : 'Load More'}
        </button>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <p
          className="text-center text-sm py-8"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          No scores yet. Be the first!
        </p>
      )}
    </div>
  );
};
